import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight, ChevronDown, Pencil, Archive, UserPlus,
} from 'lucide-react';

import Tooltip from '../../../components/ui/Tooltip';

// Skorlama Plan 4, Task 4 — girintili liste ağacı (org-chart bağımlılığı
// BİLİNÇLİ OLARAK yok — brief "hafif tut" diyor). Kendi kendini çağıran
// (recursive) düğüm bileşeni: her düğüm kendi satırını + (varsa) hizalı
// bireysel hedef özetini + (genişse) çocuklarını render eder.
const NodeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px 10px ${({ $depth }) => 14 + $depth * 24}px;
  border-bottom: 1px solid #EEF1F4;
`;

const ExpandBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: #64748b;
  flex-shrink: 0;
`;

const Spacer = styled.div`
  width: 20px;
  flex-shrink: 0;
`;

const OWNER_TYPE_COLORS = {
  company: { bg: '#E0E7FF', color: '#3730A3' },
  department: { bg: '#DCFCE7', color: '#166534' },
  team: { bg: '#FEF3C7', color: '#92400E' },
};

const OwnerBadge = styled.span`
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  background: ${({ $type }) => (OWNER_TYPE_COLORS[$type] || OWNER_TYPE_COLORS.team).bg};
  color: ${({ $type }) => (OWNER_TYPE_COLORS[$type] || OWNER_TYPE_COLORS.team).color};
`;

const TitleBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const TitleText = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubText = styled.div`
  font-size: 11px;
  color: #94A3B8;
`;

const ArchivedTag = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #B02A37;
  background: #F8D7DA;
  padding: 1px 6px;
  border-radius: 8px;
  margin-left: 6px;
`;

const ModeBadge = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  background: #F1F5F9;
  padding: 2px 8px;
  border-radius: 8px;
`;

const ProgressWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 140px;
  flex-shrink: 0;
`;

const ProgressBarOuter = styled.div`
  flex: 1;
  height: 6px;
  background: #F1F5F9;
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressBarInner = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.colors.primary.main};
  width: ${(p) => p.$pct}%;
`;

const ProgressLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  width: 36px;
  text-align: right;
  flex-shrink: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 4px;
  flex-shrink: 0;
`;

const IconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid #E0E4E8;
  border-radius: 4px;
  background: none;
  cursor: pointer;
  color: ${({ $color }) => $color || '#64748b'};
  &:hover { background: #F5F7FA; }
`;

const AlignedGoalsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 14px 10px ${({ $depth }) => 14 + $depth * 24 + 30}px;
  font-size: 11px;
  color: #64748b;
  border-bottom: 1px solid #EEF1F4;
`;

const AlignedChip = styled.span`
  background: #F5F7FA;
  border-radius: 8px;
  padding: 2px 8px;
`;

const MoreTag = styled.span`
  color: #94A3B8;
  font-style: italic;
`;

// 0-100 aralığına kırpar — MyPeriodResultsPage'in ProgressBarInner deseninin
// aynısı (clampPct), yalnız buradaki node.progressPct zaten backend'de
// hesaplanmış (rollup/manuel) bir değer.
const clampPct = (pct) => Math.min(100, Math.max(0, Number(pct) || 0));

export default function OrgGoalTreeNode({
  node, depth, canWritePm, collapsedIds, onToggleExpand, onEdit, onArchive, onAssignEmployees,
}) {
  const { t } = useTranslation();
  const hasChildren = !!(node.children && node.children.length > 0);
  const expanded = hasChildren && !collapsedIds.has(node.id);
  const isArchived = node.status === 'archived';
  // Buton görünürlüğü (task-4-brief.md): company/department → canWrite('PM');
  // team → HERKESE görünür (self-servis, BE gerçek sahiplik dışındaki
  // isteği reddeder — buton yönetici-olmayanda gizlenemiyorsa BE hatası yeter).
  const canEditNode = !isArchived && (node.ownerType === 'team' || canWritePm);
  const subText = node.ownerType === 'department' ? node.departmentName : node.ownerEmployeeName;
  const visibleAligned = node.alignedGoals || [];
  const alignedGoalCount = node.alignedGoalCount || 0;
  // Yetki-filtreli sayım: BE görmediğimiz hizalı hedefleri AlignedGoals'a hiç
  // koymaz ama AlignedGoalCount'tan asla düşmez — fark varsa "+N diğer" göster
  // (task-4-brief.md, count-fallback kuralı). FE ek görünürlük filtresi YAPMAZ,
  // geleni olduğu gibi render eder.
  const moreCount = alignedGoalCount - visibleAligned.length;

  return (
    <div data-role="org-goal-node" data-node-id={node.id}>
      {/* "own" satırları (NodeRow + AlignedGoalsRow) ayrı bir işaretleyici div'de —
          çocuklar bu düğümün DOM'u içine iç içe render edildiği için (recursive),
          bu ayrım olmadan `[data-node-id=X] [data-role=Y]` seçicisi torunların
          satırlarını da yakalardı (bkz. OrgGoalsPage.test.js: `>` direkt-çocuk
          birleştiricisiyle yalnız BU düğümün own satırına scope edilir). */}
      <div data-role="node-own">
        <NodeRow $depth={depth}>
          {hasChildren ? (
            <ExpandBtn
              data-role="expand-toggle"
              onClick={() => onToggleExpand(node.id)}
              title={expanded ? t('performance.orgGoals.collapse') : t('performance.orgGoals.expand')}
            >
              {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </ExpandBtn>
          ) : <Spacer />}

          <OwnerBadge $type={node.ownerType}>
            {t(`performance.orgGoals.ownerType.${node.ownerType}`, { defaultValue: node.ownerType })}
          </OwnerBadge>

          <TitleBlock>
            <TitleText>
              {node.title}
              {isArchived && <ArchivedTag>{t('performance.orgGoals.archivedTag')}</ArchivedTag>}
            </TitleText>
            {subText && <SubText>{subText}</SubText>}
          </TitleBlock>

          <ProgressWrap>
            <ProgressBarOuter>
              <ProgressBarInner $pct={node.progressPct != null ? clampPct(node.progressPct) : 0} />
            </ProgressBarOuter>
            <ProgressLabel data-role="progress-label">
              {node.progressPct != null ? `${node.progressPct}%` : '-'}
            </ProgressLabel>
          </ProgressWrap>

          <ModeBadge data-role="progress-mode-badge">
            {t(`performance.orgGoals.progressMode.${node.progressMode}`, { defaultValue: node.progressMode })}
          </ModeBadge>

          {canEditNode && (
            <Actions>
              {/* Müşteri önerisi (2026-08-10): kurumsal hedefi seçili çalışanlara indir —
                  toplu atama BE'de PM.Write kapılı olduğundan yalnız canWritePm'de görünür. */}
              {canWritePm && (
                <Tooltip text={t('performance.orgGoals.assignToEmployees')}>
                  <IconBtn data-role="assign-employees-btn" $color="#0291EF" onClick={() => onAssignEmployees(node)}>
                    <UserPlus size={13} />
                  </IconBtn>
                </Tooltip>
              )}
              <Tooltip text={t('performance.common.edit')}>
                <IconBtn data-role="edit-btn" $color="#F59E0B" onClick={() => onEdit(node)}>
                  <Pencil size={13} />
                </IconBtn>
              </Tooltip>
              <Tooltip text={t('performance.orgGoals.archive')}>
                <IconBtn data-role="archive-btn" $color="#94A3B8" onClick={() => onArchive(node)}>
                  <Archive size={13} />
                </IconBtn>
              </Tooltip>
            </Actions>
          )}
        </NodeRow>

        {alignedGoalCount > 0 && (
          <AlignedGoalsRow $depth={depth} data-role="aligned-goals-row">
            <span>{t('performance.orgGoals.alignedGoalsLabel')}:</span>
            {visibleAligned.map((g) => (
              <AlignedChip key={g.id}>
                {g.title} — {g.employeeName} ({g.progressPct != null ? `${g.progressPct}%` : '-'})
              </AlignedChip>
            ))}
            {moreCount > 0 && (
              <MoreTag data-role="aligned-goals-more">
                +{moreCount} {t('performance.orgGoals.alignedGoalsMore')}
              </MoreTag>
            )}
          </AlignedGoalsRow>
        )}
      </div>

      {expanded && node.children.map((child) => (
        <OrgGoalTreeNode
          key={child.id}
          node={child}
          depth={depth + 1}
          canWritePm={canWritePm}
          collapsedIds={collapsedIds}
          onToggleExpand={onToggleExpand}
          onEdit={onEdit}
          onArchive={onArchive}
          onAssignEmployees={onAssignEmployees}
        />
      ))}
    </div>
  );
}
